import { Link } from 'react-router';
import {
  SEO,
  BreadcrumbSchema,
  CollectionPageSchema,
  ItemListSchema,
  SpeakableSchema,
} from '@/components/seo/seo';
import { pressReleasesByDateDesc, type PressRelease } from '@/data/press-releases';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://fixtyer.co.uk';

function formatDate(date: string) {
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    // Date-only strings parse as UTC midnight; format in UTC so the
    // prerendered HTML matches the client in every timezone (hydration).
    timeZone: 'UTC',
  }).format(new Date(date));
}

function PressReleaseCard({ release }: { release: PressRelease }) {
  return (
    <article className="group bg-white rounded-2xl border-2 border-surface-200 hover:border-brand-300 hover:shadow-lg transition-all duration-300">
      <div className="p-6 sm:p-8">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="text-xs font-semibold px-3 py-1 rounded-full bg-brand-100 text-brand-700">
            {release.displayDate}
          </span>
          <time dateTime={release.date} className="text-sm text-surface-600">
            {formatDate(release.date)}
          </time>
          {release.locationDateline && (
            <span className="text-sm text-surface-600">{release.locationDateline}</span>
          )}
        </div>
        <h2 className="font-display text-xl sm:text-2xl font-bold text-brand-950 mb-3 group-hover:text-brand-700 transition-colors">
          <Link to={`/press/${release.slug}`}>{release.title}</Link>
        </h2>
        <p className="text-surface-700 leading-relaxed mb-5">{release.standfirst}</p>
        <Link
          to={`/press/${release.slug}`}
          className="inline-flex items-center gap-1 text-brand-600 font-medium text-sm group-hover:text-accent-600 transition-colors"
        >
          Read release
          <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </Link>
      </div>
    </article>
  );
}

export function PressIndexPage() {
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Press Releases', url: '/press' },
  ];
  const releases = pressReleasesByDateDesc;

  return (
    <div className="relative">
      <SEO
        title="Press Releases | Fixtyer Newsroom"
        description="Fixtyer press releases on UK pothole damage claims, council road maintenance, and Department for Transport road condition data. Newest first."
        canonical="/press"
      />
      {/* RSS feed for the releases collection (generated at build time). */}
      <link
        rel="alternate"
        type="application/rss+xml"
        title="Fixtyer Press Releases"
        href={`${SITE_URL}/press/feed.xml`}
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <CollectionPageSchema
        name="Fixtyer Press Releases"
        description="Press releases from Fixtyer on UK pothole damage claims and road maintenance data."
        url="/press"
      />
      {releases.length > 0 && (
        <ItemListSchema
          name="Fixtyer Press Releases"
          description="Press releases from Fixtyer, newest first."
          url="/press"
          items={releases.map((release) => ({
            name: release.title,
            url: `/press/${release.slug}`,
            description: release.standfirst,
          }))}
        />
      )}
      <SpeakableSchema
        url="/press"
        headline="Fixtyer Press Releases"
        cssSelectors={['[data-speakable="press-summary"]']}
      />

      <section className="relative overflow-hidden bg-gradient-to-br from-brand-50 via-white to-accent-50 py-16 lg:py-20">
        <div
          className="absolute top-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="absolute inset-0 opacity-30 mt-8">
          <svg className="absolute w-full h-full" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <defs>
              <pattern id="press-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-brand-200" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#press-grid)" />
          </svg>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-brand-600 mb-6">
            <Link to="/" className="hover:text-brand-700 transition-colors">
              Home
            </Link>
            <svg className="w-4 h-4 text-surface-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-surface-800">Press Releases</span>
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-100 rounded-full text-brand-700 text-sm font-medium mb-6">
            Fixtyer newsroom
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold text-brand-950 leading-tight mb-6">
            Press Releases
          </h1>
          <p data-speakable="press-summary" className="text-lg text-surface-800 max-w-2xl mx-auto">
            News and announcements from Fixtyer on UK pothole damage claims, council road
            maintenance, and Department for Transport road condition data. Journalists can reach us
            through our{' '}
            <Link
              to="/media-enquiries"
              className="font-medium text-brand-600 hover:text-brand-700 underline decoration-brand-300 underline-offset-2"
            >
              media enquiries
            </Link>{' '}
            page.
          </p>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {releases.length > 0 ? (
            <div className="space-y-6">
              {releases.map((release) => (
                <PressReleaseCard key={release.slug} release={release} />
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-surface-200 bg-surface-50 p-8 text-center">
              <h2 className="font-display text-xl font-bold text-brand-950 mb-2">
                No press releases yet
              </h2>
              <p className="text-surface-700">
                Our latest announcements will appear here. In the meantime, journalists can contact
                us through our{' '}
                <Link
                  to="/media-enquiries"
                  className="font-medium text-brand-600 hover:text-brand-700 underline decoration-brand-300 underline-offset-2"
                >
                  media enquiries
                </Link>{' '}
                page.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
